import { useRef } from 'react';

import { Box, BoxProps } from '@chakra-ui/react';
import { motion, useInView } from 'framer-motion';

interface OdometerProps extends Omit<BoxProps, 'transition'> {
  value: number;
  prefix?: string;
  suffix?: string;
  as?: React.ElementType;
}

const MotionBox = motion(Box);

export const SlidingCounter = ({
  value,
  prefix = '',
  suffix = '',
  as = 'div',
  ...boxProps
}: OdometerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });
  const decimalPlaces = (value.toString().split('.')[1] || '').length;
  const formattedValue = value
    .toFixed(decimalPlaces)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const digits = Array.from(formattedValue.toString());

  return (
    <Box ref={ref} as={as} overflow="hidden" whiteSpace="nowrap" {...boxProps}>
      {prefix && <Box as="span">{prefix}</Box>}

      {digits.map((char, index) => {
        if (!/\d/.test(char)) {
          return (
            <Box key={`char-${index}`} as="span">
              {char}
            </Box>
          );
        }

        const targetDigit = parseInt(char, 10);
        const digitCount = targetDigit + 1 + 10 * Math.min(index, 2);
        const digitSequence = Array.from(Array(digitCount).keys());

        return (
          <Box key={`char-${index}`} display="inline-block" position="relative">
            <Box as="span" visibility="hidden">
              {char}
            </Box>

            <MotionBox
              as="span"
              position="absolute"
              left="0"
              top="0"
              display="flex"
              flexDirection="column"
              initial={{ transform: 'translateY(0)' }}
              animate={{
                transform: isInView ? 'translateY(-100%)' : 'translateY(0)',
              }}
              transition={{
                delay: 0.3,
                duration: 2,
                ease: [0.4, 0.08, 0.04, 1],
              }}
            >
              {digitSequence.map((num, digitIndex) => (
                <Box
                  key={`num-${digitIndex}-${index}`}
                  as="span"
                  position={
                    digitIndex === digitSequence.length - 1
                      ? 'absolute'
                      : undefined
                  }
                  bottom={
                    digitIndex === digitSequence.length - 1 ? 0 : undefined
                  }
                  transform={
                    digitIndex === digitSequence.length - 1
                      ? 'translateY(100%)'
                      : undefined
                  }
                >
                  {num % 10}
                </Box>
              ))}
            </MotionBox>
          </Box>
        );
      })}

      {suffix && <Box as="span">{suffix}</Box>}
    </Box>
  );
};
